import React, {
  useMemo,
  ReactNode,
  useCallback,
  createContext,
  useContext,
  useState,
} from "react";

import { useAppRouteParams } from "../../../AppRoutes";
import { MemberRaw } from "../../../domain/Member";
import { User } from "../../../domain/User";
import { ViewTypeSessionData } from "../../../domain/ViewType";
import { useOrganizationSession } from "../../InOrganizationProvider/SessionProvider";
import { getViewSessionKey } from "../../InOrganizationProvider/SessionProvider/helpers";
import {
  useAccessUsersInVersion,
  useOtherSessionsData,
} from "../../InOrganizationProvider/SessionProvider/hooks";

import { useVersionPermission } from "@/hooks/useCollectionPermission";

const ctx = createContext<{
  viewSessionData: ViewTypeSessionData;
  updateViewSessionData: (viewSessionData: ViewTypeSessionData) => void;
  otherSessionsData: {
    id: string;
    data: ViewTypeSessionData;
    user: User;
  }[];
  accessMembersRaw: MemberRaw[];
  isDisableOperateView: boolean;
}>({
  viewSessionData: {
    selectingSheetId: null,
    selectingRowIds: [],
    selectingFieldId: null,
  },
  updateViewSessionData: () => {
    //nop
  },
  otherSessionsData: [],
  accessMembersRaw: [],
  isDisableOperateView: true,
});

/**
 * @description view配下で利用する、sessionData、sheet操作可能権限の管理
 */
export const ViewContextProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { viewId, versionId } = useAppRouteParams();
  const [hasVersionWritePermission] = useVersionPermission("write");
  const { sessions, sessionId } = useOrganizationSession();

  const accessMembersRaw = useAccessUsersInVersion({
    prefix: "views__",
    targetDataId: viewId,
    targetVersionId: versionId,
  });

  const [viewSessionData, setViewSessionData] = useState<ViewTypeSessionData>({
    selectingSheetId: null,
    selectingRowIds: [],
    selectingFieldId: null,
  });
  const updateViewSessionData = useCallback(
    (viewSessionData: ViewTypeSessionData) => {
      setViewSessionData(viewSessionData);
    },
    [setViewSessionData]
  );

  const sessionKey = getViewSessionKey(viewId);
  const otherSessionsData = useOtherSessionsData<ViewTypeSessionData>(
    sessions,
    sessionKey,
    sessionId,
    versionId
  );

  const value = useMemo(
    () => ({
      viewSessionData,
      updateViewSessionData,
      otherSessionsData,
      accessMembersRaw,
      isDisableOperateView: !hasVersionWritePermission,
    }),
    [
      viewSessionData,
      updateViewSessionData,
      otherSessionsData,
      accessMembersRaw,
      hasVersionWritePermission,
    ]
  );

  return <ctx.Provider value={value}>{children}</ctx.Provider>;
};

export const useViewContext = () => {
  return useContext(ctx);
};
